import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { ErrorBox, Loading, PageTitle } from '@/components/ui';

type SkillType = 'disrupt' | 'shield';

interface SkillAssetItem {
  skill: SkillType;
  imageUrl: string | null;
}

const SKILL_META: Record<SkillType, { label: string; color: string; hint: string }> = {
  disrupt: { label: 'DISRUPT', color: '#9B5DE5', hint: 'Scrambles the opponent’s next holds.' },
  shield: { label: 'SHIELD', color: '#22D3EE', hint: 'Blocks an incoming disrupt.' },
};

async function req<T>(method: string, path: string, body?: unknown, isForm = false): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  if (!isForm) headers['Content-Type'] = 'application/json';
  const res = await fetch(path, {
    method, headers,
    body: isForm ? (body as FormData) : body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let d = '';
    try { const j = await res.json(); d = j?.message ?? j?.error ?? ''; } catch { /* */ }
    throw new Error(d || `HTTP ${res.status}`);
  }
  return res.json();
}

export function SkillsPage() {
  const [items, setItems] = useState<SkillAssetItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    req<{ items: SkillAssetItem[] }>('GET', '/admin/skills/')
      .then(({ items }) => setItems(items))
      .catch((e) => setErr(String(e)));

  useEffect(() => { void load(); }, []);

  if (err) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  return (
    <div>
      <PageTitle title="Skill Skins" />
      <div className="card" style={{ padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--ink-muted)', background: 'rgba(34,211,238,0.08)', borderColor: 'rgba(34,211,238,0.25)' }}>
        Upload one PNG per skill (Disrupt / Shield). It replaces the built-in SVG icon everywhere
        the skill appears — in-match buttons, Home and the Shop. Transparent PNG recommended; the
        image is auto-optimized to a 256px WebP. Leave empty to keep the default SVG.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {items.map((it) => <SkillRow key={it.skill} item={it} onChanged={load} />)}
      </div>
    </div>
  );
}

function SkillRow({ item, onChanged }: { item: SkillAssetItem; onChanged: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const meta = SKILL_META[item.skill];

  async function upload(f: File) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await req('POST', `/admin/skills/${item.skill}/image`, fd, true);
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function clear() {
    if (!confirm(`Remove the ${meta.label} skin and revert to the default icon?`)) return;
    setBusy(true);
    try { await req('DELETE', `/admin/skills/${item.skill}/image`); await onChanged(); }
    catch (e: any) { alert(e?.message); }
    setBusy(false);
  }

  return (
    <div className="card" style={{ padding: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 200px' }}>
        <div className="display" style={{ fontSize: 16, color: meta.color, letterSpacing: '0.08em' }}>{meta.label}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 4 }}>{meta.hint}</div>
      </div>

      <div style={{ width: 96, height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', borderRadius: 10, overflow: 'hidden', border: `1px solid ${meta.color}40` }}>
        {item.imageUrl
          ? <img src={item.imageUrl} alt="" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
          : <span style={{ fontSize: 10, color: 'var(--ink-dim)' }}>default SVG</span>}
      </div>

      <input
        type="file"
        accept="image/png,image/webp,image/gif"
        ref={fileRef}
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button className="btn btn-primary" onClick={() => fileRef.current?.click()} disabled={busy}>
          {item.imageUrl ? 'REPLACE' : 'UPLOAD PNG'}
        </button>
        {item.imageUrl && (
          <button className="btn" onClick={clear} disabled={busy} style={{ color: 'var(--c-red)' }}>REMOVE</button>
        )}
      </div>
    </div>
  );
}
